import { Injectable, inject } from '@angular/core';
import { Firestore, collection, query, where, getDocs, doc, setDoc, writeBatch, deleteDoc, updateDoc, arrayUnion, limit, onSnapshot, getDoc, orderBy, addDoc } from '@angular/fire/firestore';
import { Observable, map, firstValueFrom } from 'rxjs';
import { Invoice } from '../models/invoice.model';
import { SmrsRate } from './fee-rate.service';
import { StoreService } from './store.service';
import { deviceBelongsToBrand } from './device-brand.util';
import {
  ForwardInvoice, GeneratedTotals, GenerationClass, aggregationBelongsToCompany, classifyCompanyPeriod,
  computeGeneratedTotals, deriveStatus, generatedRowsMatch, isGeneratedAggregation, simulateForwardBalances, willGenerate
} from './smrs-generation.util';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  private firestore = inject(Firestore);
  private storeService = inject(StoreService);

  getCompanies(): Observable<any[]> {
    const col = collection(this.firestore, 'company');
    return new Observable<any[]>(observer => {
      const unsubscribe = onSnapshot(col, (snapshot) => {
        const companies = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const filtered = companies.filter((c: any) => c['isDeleted'] !== true);
        observer.next(filtered);
      }, (error) => observer.error(error));
      return unsubscribe;
    });
  }

  getStores(): Observable<any[]> {
    return this.storeService.getStores();
  }

  async getAggregations(period: string): Promise<any[]> {
    const aggRef = collection(this.firestore, 'monthly_store_aggregations');
    let qAgg;
    if (period === 'All Periods') {
      qAgg = query(aggRef);
    } else {
      qAgg = query(aggRef, where('period', '==', period));
    }
    const snap = await getDocs(qAgg);
    return snap.docs.map(d => d.data());
  }

  async getAggregationsForStore(period: string, storeId: string): Promise<any[]> {
    const aggRef = collection(this.firestore, 'monthly_store_aggregations');
    const qAgg = query(aggRef, where('period', '==', period), where('storeId', '==', storeId));
    const snap = await getDocs(qAgg);
    return snap.docs.map(d => d.data());
  }

  async getInvoices(period?: string, companyId?: string): Promise<any[]> {
    const invoicesCol = collection(this.firestore, 'invoices');
    const constraints = [];
    if (period) constraints.push(where('period', '==', period));
    if (companyId) constraints.push(where('companyId', '==', companyId));
    
    const qInv = query(invoicesCol, ...constraints);
    const snap = await getDocs(qInv);
    return snap.docs.map(d => ({ ...d.data(), id: d.id, ref: d.ref }));
  }

  async getInvoiceHistory(invoiceId: string): Promise<any[]> {
    const historyCol = collection(this.firestore, `invoices/${invoiceId}/history`);
    const q = query(historyCol, orderBy('activityDate', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  async getExchangeRate(currency: string, period: string): Promise<{ rate: number, date?: Date } | null> {
    const ratesCol = collection(this.firestore, 'exchange_rates');
    const q = query(ratesCol, where('period', '==', period), where('currency', '==', currency));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const data = snapshot.docs[0].data();
      let date = data['date'];
      if (date && typeof date.toDate === 'function') {
        date = date.toDate();
      } else if (date) {
        date = new Date(date);
      }
      return { rate: Number(data['rate']), date };
    }
    return null;
  }

  getPreviousPeriod(period: string): string {
    if (!period || !period.includes('/')) return '';
    const parts = period.split('/');
    let year = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);

    month -= 1;
    if (month === 0) {
      month = 12;
      year -= 1;
    }
    return `${year}/${month.toString().padStart(2, '0')}`;
  }

  async getBeginningBalance(companyId: string, currentPeriod: string): Promise<number> {
    if (!companyId || !currentPeriod) return 0;

    try {
      const invoicesCol = collection(this.firestore, 'invoices');
      // Fetch prior invoices for this company
      const q = query(
        invoicesCol,
        where('companyId', '==', companyId),
        where('period', '<', currentPeriod)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) return 0;

      // Filter out canceled invoices and sort descending by period to find the immediate previous period
      const validPriorDocs = snapshot.docs
        .map(d => d.data())
        .filter(d => d['status'] !== 'Canceled')
        .sort((a, b) => (b['period'] || '').localeCompare(a['period'] || ''));

      if (validPriorDocs.length === 0) return 0;

      const last = validPriorDocs[0];
      return Number(last['remainingBalance']) || 0;
    } catch (error) {
      console.error('Error fetching beginning balance:', error);
      return 0;
    }
  }

  async propagateBalanceUpdate(companyId: string, currentPeriod: string, delta: number, batch: any) {
    const invoicesRef = collection(this.firestore, 'invoices');
    const q = query(invoicesRef, where('companyId', '==', companyId), where('period', '>', currentPeriod));
    const snapshot = await getDocs(q);
    
    const docs = snapshot.docs.map(d => ({ data: d.data(), ref: d.ref }));
    docs.sort((a, b) => a.data['period'].localeCompare(b.data['period']));

    for (const doc of docs) {
      const data = doc.data;

      if (data['beginningBalanceOverride'] !== undefined) {
        break;
      }

      const oldRem = data['remainingBalance'] || 0;
      const newRem = Math.round((oldRem + delta) * 100) / 100;
      const oldStatus = data['status'];
      
      let newStatus = oldStatus;
      if (oldStatus === 'Not Paid') {
         if (newRem <= 0.01) newStatus = 'Paid';
         else newStatus = 'Not Paid';
      } else {
         if (newRem <= 0.01) newStatus = 'Paid';
         else newStatus = 'Partial';
      }
      
      batch.update(doc.ref, {
        remainingBalance: newRem,
        status: newStatus
      });
    }
  }

  /**
   * Check if any invoices exist for periods after the given period for the specified brand's companies.
   * Used as a guardrail to prevent re-importing data when future months already have data.
   */
  async hasFutureInvoices(period: string, brandCompanyIds: string[]): Promise<{ hasFuture: boolean, latestPeriod: string }> {
    if (!period || brandCompanyIds.length === 0) {
      return { hasFuture: false, latestPeriod: '' };
    }

    const invoicesCol = collection(this.firestore, 'invoices');
    // Firestore 'in' queries are limited to 30 values, so chunk if needed
    const chunks: string[][] = [];
    for (let i = 0; i < brandCompanyIds.length; i += 30) {
      chunks.push(brandCompanyIds.slice(i, i + 30));
    }

    let latestPeriod = '';
    for (const chunk of chunks) {
      const q = query(invoicesCol, where('companyId', 'in', chunk), where('period', '>', period));
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(d => {
        const p = d.data()['period'] || '';
        if (p > latestPeriod) latestPeriod = p;
      });
    }

    return { hasFuture: latestPeriod !== '', latestPeriod };
  }

  /**
   * Get detailed list of future invoice periods and company IDs for historical import review modal.
   */
  async getFutureInvoicesDetails(period: string, brandCompanyIds: string[]): Promise<{ futurePeriods: string[], affectedCompanyIds: string[] }> {
    if (!period || brandCompanyIds.length === 0) {
      return { futurePeriods: [], affectedCompanyIds: [] };
    }

    const invoicesCol = collection(this.firestore, 'invoices');
    const chunks: string[][] = [];
    for (let i = 0; i < brandCompanyIds.length; i += 30) {
      chunks.push(brandCompanyIds.slice(i, i + 30));
    }

    const periodsSet = new Set<string>();
    const companiesSet = new Set<string>();

    for (const chunk of chunks) {
      const q = query(invoicesCol, where('companyId', 'in', chunk), where('period', '>', period));
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(d => {
        const data = d.data();
        const p = data['period'];
        const cid = data['companyId'];
        if (p) periodsSet.add(p);
        if (cid) companiesSet.add(cid);
      });
    }

    return {
      futurePeriods: Array.from(periodsSet).sort(),
      affectedCompanyIds: Array.from(companiesSet)
    };
  }

  /**
   * Core ledger recalculation: recompute beginningBalance, totalFee, totalDue, remainingBalance
   * for a company starting from `startPeriod` and propagating forward through all subsequent periods.
   * 
   * This replaces the flawed delta-based propagateBalanceUpdate by computing from source-of-truth
   * aggregation data. amountPaid is NEVER modified — it's a locked payment fact.
   * 
   * @param companyId The company to recalculate
   * @param startPeriod The period to start recalculating from (inclusive)
   * @param allStores All stores (used to map stores to companies)
   * @param allRates All rates (used to calculate fees from aggregations)
   * @param externalBatch Optional existing WriteBatch to add updates to. If not provided, creates and commits its own.
   */
  /**
   * Core ledger recalculation: recompute beginningBalance, totalFee, totalDue, remainingBalance
   * for a company starting from `startPeriod` and propagating forward through all subsequent periods.
   * 
   * This replaces the flawed delta-based propagateBalanceUpdate by computing from source-of-truth
   * aggregation data. amountPaid is NEVER modified — it's a locked payment fact.
   * 
   * @param companyId The company to recalculate
   * @param startPeriod The period to start recalculating from (inclusive)
   * @param allStores Optional all stores (if omitted, fetched from Firestore)
   * @param allRates Optional all rates (if omitted, fetched from Firestore)
   * @param externalBatch Optional existing WriteBatch to add updates to. If not provided, creates and commits its own.
   */
  async recalculateLedgerForward(
    companyId: string, 
    startPeriod: string, 
    allStores?: any[], 
    allRates?: SmrsRate[],
    externalBatch?: any
  ): Promise<void> {
    if (!companyId || !startPeriod) return;

    const invoicesCol = collection(this.firestore, 'invoices');

    // 1. Fetch stores if not provided
    if (!allStores || allStores.length === 0) {
      const storeSnap = await getDocs(collection(this.firestore, 'stores'));
      allStores = storeSnap.docs.map(d => ({ ...d.data(), id: d.id }));
    }

    // 2. Fetch rates if not provided
    if (!allRates || allRates.length === 0) {
      const ratesSnap = await getDocs(collection(this.firestore, 'smrs_rates'));
      allRates = ratesSnap.docs.map(d => ({ ...d.data(), id: d.id } as SmrsRate));
    }

    // 3. Check if company is fee collection
    let isFeeCollection = false;
    try {
      const companies = await firstValueFrom(this.getCompanies());
      const comp = companies.find((c: any) => c.id === companyId);
      isFeeCollection = comp?.isFeeCollection === true;
    } catch (e) {
      console.warn('Could not determine fee collection status for company:', companyId, e);
    }

    // 4. Get the beginning balance from the most recent period BEFORE startPeriod
    let carryForwardBalance = 0;
    if (!isFeeCollection) {
      carryForwardBalance = await this.getBeginningBalance(companyId, startPeriod);
    }

    // 5. Fetch existing invoices for this company from startPeriod onward, sorted chronologically
    const qForward = query(invoicesCol, where('companyId', '==', companyId), where('period', '>=', startPeriod));
    const forwardSnap = await getDocs(qForward);
    const invoiceDocs = forwardSnap.docs
      .map(d => ({ ref: d.ref, data: d.data(), id: d.id }))
      .sort((a, b) => (a.data['period'] || '').localeCompare(b.data['period'] || ''));

    // 6. Build a set of company store IDs for filtering aggregations
    const companyStoreIds = new Set<string>();
    allStores.forEach(s => {
      const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
      if (cids.includes(companyId)) {
        const sid = String(s['storeId'] || s['id'] || '').trim();
        if (sid) companyStoreIds.add(sid);
      }
    });

    // 7. Discover all relevant periods from both existing invoices and store aggregations
    const aggCol = collection(this.firestore, 'monthly_store_aggregations');
    const allAggSnap = await getDocs(aggCol);
    const allAggDocs: any[] = [];
    const allPeriodsSet = new Set<string>();

    invoiceDocs.forEach(d => {
      const p = d.data['period'];
      if (p && p >= startPeriod) allPeriodsSet.add(p);
    });

    allAggSnap.docs.forEach(d => {
      const data = d.data();
      const sid = String(data['storeId'] || '').trim();
      const p = data['period'] || '';
      const belongs = (data['companyId'] === companyId) || (companyStoreIds.has(sid) && !data['companyId']);
      if (belongs && p >= startPeriod) {
        allAggDocs.push(data);
        allPeriodsSet.add(p);
      }
    });

    const periodsToRecalc = Array.from(allPeriodsSet).sort();
    if (periodsToRecalc.length === 0) return;
    // 8. Walk through periods chronologically and recalculate
    const batch = externalBatch || writeBatch(this.firestore);

    for (const period of periodsToRecalc) {
      const existingDocItem = invoiceDocs.find(d => d.data['period'] === period);
      const data = existingDocItem ? existingDocItem.data : {};
      const currentStatus = data['status'];
      const docRef = existingDocItem ? existingDocItem.ref : doc(invoicesCol);

      // Skip canceled invoices - they don't affect the forward chain
      if (currentStatus === 'Canceled') {
        carryForwardBalance = 0;
        continue;
      }

      if (isFeeCollection) {
        batch.set(docRef, {
          companyId: companyId,
          period: period,
          beginningBalance: 0,
          totalFee: 0,
          totalRevenue: 0,
          totalLfcFee: 0,
          totalKcegFee: 0,
          totalDue: 0,
          remainingBalance: 0,
          amountPaid: 0,
          status: 'Paid',
          updatedAt: new Date(),
          ...(data['createdAt'] ? {} : { createdAt: new Date() })
        }, { merge: true });
        carryForwardBalance = 0;
        continue;
      }

      // Determine beginning balance
      let beginningBalance: number;
      if (data['beginningBalanceOverride'] !== undefined) {
        beginningBalance = Number(data['beginningBalanceOverride']);
      } else {
        beginningBalance = carryForwardBalance;
      }

      let totalFee = 0;
      let totalRevenue = 0;
      let totalLfcFee = 0;
      let totalKcegFee = 0;

      // Invariant: Past invoice edits/creations NEVER alter future periods' revenues or fees!
      // If the invoice already has a valid fee, preserve its revenue and fee fields untouched.
      const hasExistingFee = data['totalFee'] !== undefined && data['totalFee'] !== null;
      const hasExistingRevenue = data['totalRevenue'] !== undefined && data['totalRevenue'] !== null;

      if (hasExistingFee) {
        totalFee = Number(data['totalFee']);
        totalRevenue = hasExistingRevenue ? Number(data['totalRevenue']) : totalFee;
        totalLfcFee = Number(data['totalLfcFee'] ?? 0);
        totalKcegFee = Number(data['totalKcegFee'] ?? 0);
      } else {
        // Only if totalFee is missing or 0 (e.g. un-generated invoice or corrupted zero),
        // compute from source-of-truth monthly store aggregations.
        const periodAggs = allAggDocs.filter(a => a['period'] === period);
        const periodRates = allRates.filter(r => r.period === period);
        let matchedAggs = false;

        periodAggs.forEach(d => {
          const sid = String(d['storeId'] || '').trim();
          const belongsToCompany = (d['companyId'] === companyId) || 
            (companyStoreIds.has(sid) && !d['companyId']);

          if (belongsToCompany && d['revenues'] && !d['isAdjustment']) {
            matchedAggs = true;
            periodRates.forEach(r => {
              const val = this.calculateRevenueForRate(d, r);
              totalRevenue += val;
              const currentFee = val * (r.rate / 100);
              totalFee += currentFee;

              const kcegRateVal = (r as any).kcegRate !== undefined && (r as any).kcegRate !== null ? Number((r as any).kcegRate) : 0;
              const lfcRateVal = (r.rate || 0) - kcegRateVal;

              if ((r.rate || 0) !== 0) {
                totalKcegFee += currentFee * (kcegRateVal / r.rate);
                totalLfcFee += currentFee * (lfcRateVal / r.rate);
              }
            });
          }

          // Handle adjustments linked to this company
          if (d['isAdjustment'] && d['companyId'] === companyId && d['revenues']) {
            matchedAggs = true;
            periodRates.forEach(r => {
              const val = this.calculateRevenueForRate(d, r);
              totalRevenue += val;
              const currentFee = val * (r.rate / 100);
              totalFee += currentFee;

              const kcegRateVal = (r as any).kcegRate !== undefined && (r as any).kcegRate !== null ? Number((r as any).kcegRate) : 0;
              const lfcRateVal = (r.rate || 0) - kcegRateVal;

              if ((r.rate || 0) !== 0) {
                totalKcegFee += currentFee * (kcegRateVal / r.rate);
                totalLfcFee += currentFee * (lfcRateVal / r.rate);
              }
            });
          }
        });

        totalFee = Math.round(totalFee * 100) / 100;
        totalRevenue = Math.round(totalRevenue * 100) / 100;
        totalLfcFee = Math.round(totalLfcFee * 100) / 100;
        totalKcegFee = Math.round(totalKcegFee * 100) / 100;

        // Fallback for un-migrated invoices where no aggregation exists
        if (!matchedAggs && data['remainingBalance'] !== undefined) {
          const existingRem = Number(data['remainingBalance']);
          const existingPaid = Number(data['amountPaid'] || 0);
          totalFee = Math.max(0, Math.round((existingRem + existingPaid - beginningBalance) * 100) / 100);
          totalRevenue = Number(data['totalRevenue'] ?? totalFee);
        }
      }

      const totalDue = Math.round((beginningBalance + totalFee) * 100) / 100;
      const amountPaid = Number(data['amountPaid'] || 0);
      const remainingBalance = Math.round((totalDue - amountPaid) * 100) / 100;

      // Determine status
      let newStatus: string;
      if (remainingBalance <= 0.01) {
        newStatus = 'Paid';
      } else if (amountPaid > 0.01) {
        newStatus = 'Partial';
      } else {
        newStatus = 'Not Paid';
      }

      // Write persisted fields using batch.set with merge: true (handles existing or newly created docs)
      batch.set(docRef, {
        companyId: companyId,
        period: period,
        status: newStatus,
        emailStatus: data['emailStatus'] || 'Not Sent',
        beginningBalance: beginningBalance,
        totalFee: totalFee,
        totalRevenue: totalRevenue,
        totalLfcFee: totalLfcFee,
        totalKcegFee: totalKcegFee,
        totalDue: totalDue,
        remainingBalance: remainingBalance,
        amountPaid: amountPaid,
        updatedAt: new Date(),
        ...(data['createdAt'] ? {} : { createdAt: new Date() })
      }, { merge: true });

      // Set carry-forward for the next period
      carryForwardBalance = remainingBalance;
    }

    // Commit only if we created our own batch
    if (!externalBatch) {
      await batch.commit();
    }
  }


  private addActivityToBatch(
    batch: any,
    invoiceRef: any,
    activity: {
      category: string,
      fieldChanged?: string,
      oldValue?: any,
      newValue?: any,
      activityBy: string,
      generationRunId?: string,
      oldRemainingBalance?: number | null,
      newRemainingBalance?: number
    }
  ) {
    const now = new Date();
    const historyId = now.toISOString() + '_' + Math.random().toString(36).substr(2, 9);
    const historyRef = doc(collection(invoiceRef, 'history'), historyId);
    batch.set(historyRef, {
      ...activity,
      activityDate: now
    });
  }

  async saveManualInvoice(
    manualInvoiceData: any, 
    rates: SmrsRate[], 
    userName: string, 
    companyStores: any[], 
    beginningBalance: number
  ): Promise<void> {
    const batch = writeBatch(this.firestore);
    const period = manualInvoiceData.period;
    const periodKey = period.replace('/', '-');
    const companyId = manualInvoiceData.companyId;

    const companyStoreIds = new Set(companyStores.map(s => String(s['storeId']).trim()));
    manualInvoiceData.items.forEach((i: any) => companyStoreIds.add(i.storeId));

    // 1. Delete existing aggregations
    const aggRef = collection(this.firestore, 'monthly_store_aggregations');
    const qAgg = query(aggRef, where('period', '==', period));
    const aggSnap = await getDocs(qAgg);

    let oldTotalFee = 0;
    aggSnap.forEach(doc => {
      const d = doc.data();
      const sid = String(d['storeId'] || '').trim();
      // Delete if it's a store belonging to this company OR if it's an adjustment for this company
      if (companyStoreIds.has(sid) || (d['isAdjustment'] && d['companyId'] === companyId)) {
        batch.delete(doc.ref);
        if (d['revenues']) {
          rates.forEach(r => {
            const val = this.calculateRevenueForRate(d, r);
            oldTotalFee += val * (r.rate / 100);
          });
        }
      }
    });

    // 2. Add new aggregations
    let newTotalFee = 0;
    for (const item of manualInvoiceData.items) {
      const importId = 'manual_' + Date.now();
      const docId = `${periodKey}-${item.storeId}-${importId}`;
      const newRef = doc(this.firestore, 'monthly_store_aggregations', docId);

      for (const [rateId, amount] of Object.entries(item.revenues)) { 
        const r = rates.find(x => x.id === rateId);
        if (r) newTotalFee += (Number(amount) as number || 0) * (r.rate / 100);
      }
      
      batch.set(newRef, {
        storeId: item.storeId,
        period: period,
        revenues: item.revenues,
        importId: importId,
        companyId: companyId,   // Tag with companyId so recalculateLedgerForward can locate this agg
        sourceFileUrl: null,
        updatedAt: new Date(),
        updatedBy: userName,
        isManual: true
      });
    }

    // 2b. Add adjustments
    for (const adj of manualInvoiceData.adjustments) {
      if (!adj.name) continue;
      const importId = 'manual_adj_' + Date.now();
      const docId = `${periodKey}-ADJ-${importId}`; // Unique ID for adjustment
      const newRef = doc(this.firestore, 'monthly_store_aggregations', docId);

      for (const [rateId, amount] of Object.entries(adj.amounts)) {
        const r = rates.find(x => x.id === rateId);
        if (r) newTotalFee += (Number(amount) as number || 0) * (r.rate / 100);
      }

      batch.set(newRef, {
        storeId: adj.name, // Use name as storeId for display
        period: period,
        revenues: adj.amounts,
        importId: importId,
        companyId: companyId, // Explicitly link to company
        isManual: true,
        isAdjustment: true,
        updatedAt: new Date(),
        updatedBy: userName
      });
    }

    // 3. Update Invoice
    const invRef = collection(this.firestore, 'invoices');
    const qInv = query(invRef, where('companyId', '==', companyId), where('period', '==', period));
    const invSnap = await getDocs(qInv);

    let amountPaid = 0;
    let status = 'Not Paid';
    let existingDocRef = null;
    let emailStatus = 'Not Sent';
    let emailSentAt = null;
    let emailRecipients = '';
    let createdAt = new Date();
    let oldRemainingBalanceForDelta = 0;
    let isManual = false;
    let isEdited = false;

    let oldStatus = 'Not Paid';
    let oldPaid = 0;
    let oldRemaining = 0;

    if (!invSnap.empty) {
      const invDoc = invSnap.docs[0];
      existingDocRef = invDoc.ref;
      const invData = invDoc.data();
      
      status = invData['status'] || 'Not Paid';
      emailStatus = invData['emailStatus'] || 'Not Sent';
      emailSentAt = invData['emailSentAt'] || null;
      emailRecipients = invData['emailRecipients'] || '';
      createdAt = invData['createdAt'] || new Date();
      isManual = invData['isManual'] || false;
      isEdited = true;
      
      const oldRemainingVal = invData['remainingBalance'];
      const oldTotalDue = beginningBalance + oldTotalFee;

      oldStatus = status;
      oldPaid = invData['amountPaid'] || 0;
      oldRemaining = typeof oldRemainingVal === 'number' ? oldRemainingVal : (oldTotalDue - oldPaid);

      if (invData['amountPaid'] !== undefined) {
        amountPaid = invData['amountPaid'];
        if (typeof oldRemainingVal === 'number') {
           oldRemainingBalanceForDelta = oldRemainingVal;
        } else {
           oldRemainingBalanceForDelta = oldTotalDue - amountPaid;
        }
      } else {
        if (status === 'Paid') {
          amountPaid = oldTotalDue;
          oldRemainingBalanceForDelta = 0;
        } else if (status === 'Not Paid') {
          amountPaid = 0;
          oldRemainingBalanceForDelta = oldTotalDue;
        } else {
          if (typeof oldRemainingVal === 'number') {
            amountPaid = oldTotalDue - oldRemainingVal;
            oldRemainingBalanceForDelta = oldRemainingVal;
          } else {
            amountPaid = 0;
            oldRemainingBalanceForDelta = oldTotalDue;
          }
        }
      }
    } else {
      isManual = true;
    }

    const newTotalDue = beginningBalance + newTotalFee;
    let newRemainingBalance = Math.round((newTotalDue - amountPaid) * 100) / 100;

    if (newRemainingBalance <= 0.01) {
      status = 'Paid';
    } else if (amountPaid > 0) {
      status = 'Partial';
    } else {
      status = 'Not Paid';
    }    // Calculate fee breakdown and revenue
    let totalRevenue = 0;
    let totalLfcFee = 0;
    let totalKcegFee = 0;

    for (const item of manualInvoiceData.items) {
      for (const [rateId, amount] of Object.entries(item.revenues)) {
        const val = Number(amount) || 0;
        totalRevenue += val;
        const r = rates.find(x => x.id === rateId);
        if (r) {
          const fee = val * (r.rate / 100);
          const kcegRateVal = (r as any).kcegRate !== undefined && (r as any).kcegRate !== null ? Number((r as any).kcegRate) : 0;
          const lfcRateVal = (r.rate || 0) - kcegRateVal;
          if ((r.rate || 0) !== 0) {
            totalKcegFee += fee * (kcegRateVal / r.rate);
            totalLfcFee += fee * (lfcRateVal / r.rate);
          }
        }
      }
    }

    for (const adj of manualInvoiceData.adjustments) {
      for (const [rateId, amount] of Object.entries(adj.amounts)) {
        const val = Number(amount) || 0;
        totalRevenue += val;
        const r = rates.find(x => x.id === rateId);
        if (r) {
          const fee = val * (r.rate / 100);
          const kcegRateVal = (r as any).kcegRate !== undefined && (r as any).kcegRate !== null ? Number((r as any).kcegRate) : 0;
          const lfcRateVal = (r.rate || 0) - kcegRateVal;
          if ((r.rate || 0) !== 0) {
            totalKcegFee += fee * (kcegRateVal / r.rate);
            totalLfcFee += fee * (lfcRateVal / r.rate);
          }
        }
      }
    }

    const invoiceData: any = {
      companyId: companyId,
      period: period,
      status: status,
      emailStatus: emailStatus,
      emailSentAt: emailSentAt,
      emailRecipients: emailRecipients,
      beginningBalance: beginningBalance,
      totalFee: Math.round(newTotalFee * 100) / 100,
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      totalLfcFee: Math.round(totalLfcFee * 100) / 100,
      totalKcegFee: Math.round(totalKcegFee * 100) / 100,
      totalDue: newTotalDue,
      remainingBalance: newRemainingBalance,
      amountPaid: amountPaid,
      createdAt: createdAt,
      updatedAt: new Date(),
      updatedBy: userName,
      isManual: isManual,
      isEdited: isEdited
    };

    if (existingDocRef) {
      batch.set(existingDocRef, invoiceData, { merge: true });
      
      if (oldStatus !== status) {
        this.addActivityToBatch(batch, existingDocRef, {
          category: 'Invoice Status Change',
          fieldChanged: 'Status',
          oldValue: oldStatus,
          newValue: status,
          activityBy: userName
        });
      }
      if (Math.abs(oldPaid - amountPaid) > 0.001) {
        this.addActivityToBatch(batch, existingDocRef, {
          category: 'Payment Update',
          fieldChanged: 'Amount Paid',
          oldValue: oldPaid,
          newValue: amountPaid,
          activityBy: userName
        });
      }
      if (Math.abs(oldRemaining - newRemainingBalance) > 0.001) {
        this.addActivityToBatch(batch, existingDocRef, {
          category: 'Invoice Edit',
          fieldChanged: 'Remaining Balance',
          oldValue: oldRemaining,
          newValue: newRemainingBalance,
          activityBy: userName
        });
      }
    } else {
      const newInvRef = doc(collection(this.firestore, 'invoices'));
      invoiceData.createdBy = userName;
      batch.set(newInvRef, invoiceData);
      this.addActivityToBatch(batch, newInvRef, {
        category: 'Invoice Created',
        fieldChanged: 'Created',
        oldValue: null,
        newValue: 'Created',
        activityBy: userName
      });
    }

    await batch.commit();
    // Do NOT pass single-period rates/stores here — recalculateLedgerForward must fetch ALL rates
    // from Firestore so it can correctly compute fees for every subsequent period (e.g. 2026/02),
    // not just the period of the manual invoice (e.g. 2024/01).
    await this.recalculateLedgerForward(companyId, period);
  }

  async updateEmailStatus(row: any, recipients: string, attachmentUrl: string | null, userName: string): Promise<void> {
    const now = new Date();
    const invoicesCol = collection(this.firestore, 'invoices');
    const q = query(invoicesCol, where('period', '==', row.period), where('companyId', '==', row.company.id));
    const snapshot = await getDocs(q);

    const updateData: Partial<Invoice> = { emailStatus: 'Sent', emailSentAt: now };
    if (recipients) {
      updateData.emailRecipients = recipients;
    }

    const batch = writeBatch(this.firestore);

    if (!snapshot.empty) {
      const docRef = snapshot.docs[0].ref;
      batch.set(docRef, updateData, { merge: true });
      this.addActivityToBatch(batch, docRef, {
        category: 'Email',
        fieldChanged: 'Email Sent',
        oldValue: '',
        newValue: '',
        activityBy: userName
      });
    } else {
      const newInvoice: Invoice = {
        companyId: row.company.id,
        period: row.period,
        status: 'Not Paid',
        emailStatus: 'Sent',
        emailSentAt: now,
        emailRecipients: recipients || '',
        createdAt: now
      };
      const newDocRef = doc(invoicesCol);
      batch.set(newDocRef, newInvoice);
      this.addActivityToBatch(batch, newDocRef, {
        category: 'Email',
        fieldChanged: 'Email Sent',
        oldValue: '',
        newValue: '',
        activityBy: userName
      });
    }
    await batch.commit();
  }

  async deleteInvoice(row: any, storeIds: string[]): Promise<number> {
    const batch = writeBatch(this.firestore);

    // 1. Delete Invoice Document
    const invoicesCol = collection(this.firestore, 'invoices');
    const qInv = query(invoicesCol, where('period', '==', row.period), where('companyId', '==', row.company.id));
    const invSnap = await getDocs(qInv);
    invSnap.forEach(doc => batch.delete(doc.ref));

    // 2. Delete Aggregations and Adjustments
    const aggRef = collection(this.firestore, 'monthly_store_aggregations');
    const qAgg = query(aggRef, where('period', '==', row.period));
    const aggSnap = await getDocs(qAgg);
    const storeIdSet = new Set(storeIds.map(s => String(s).trim()));

    aggSnap.forEach(doc => {
      const d = doc.data();
      const sid = String(d['storeId'] || '').trim();
      const cid = d['companyId'];
      if (storeIdSet.has(sid) || (d['isAdjustment'] && cid === row.company.id) || (cid && cid === row.company.id)) {
        batch.delete(doc.ref);
      }
    });

    const currentRemaining = row.remainingBalance;
    const prevBalance = await this.getBeginningBalance(row.company.id, row.period);
    const delta = prevBalance - currentRemaining;

    await batch.commit();
    await this.recalculateLedgerForward(row.company.id, row.period);
    return delta;
  }

  async saveBeginningBalance(row: any, newBalance: number, totalFee: number, amountPaid: number, userName: string): Promise<void> {
    const currentBalance = row.beginningBalanceOverride !== undefined ? row.beginningBalanceOverride : row.beginningBalance;
    const delta = newBalance - currentBalance;
    const currentPeriod = row.period;
    const companyId = row.company.id;

    const batch = writeBatch(this.firestore);
    const invoicesCol = collection(this.firestore, 'invoices');
    
    const q = query(invoicesCol, where('companyId', '==', companyId), where('period', '==', currentPeriod));
    const snapshot = await getDocs(q);

    let docRef;
    let oldStatus = 'Not Paid';
    let oldPaid = 0;
    let oldRemaining = 0;

    if (!snapshot.empty) {
      docRef = snapshot.docs[0].ref;
      const d = snapshot.docs[0].data();
      oldStatus = d['status'] || 'Not Paid';
      oldPaid = d['amountPaid'] || 0;
      oldRemaining = d['remainingBalance'] || 0;
    } else {
      docRef = doc(invoicesCol);
    }

    const newTotalDue = newBalance + totalFee;
    const newRemaining = Math.round((newTotalDue - amountPaid) * 100) / 100;

    const updateData: any = {
      beginningBalance: newBalance,
      beginningBalanceOverride: newBalance,
      totalFee: Math.round(totalFee * 100) / 100,
      totalDue: newTotalDue,
      remainingBalance: newRemaining,
      amountPaid: amountPaid,
      isEdited: true,
      updatedAt: new Date(),
      updatedBy: userName
    };

    if (newRemaining <= 0.01) updateData.status = 'Paid';
    else if (amountPaid > 0.01) updateData.status = 'Partial';
    else updateData.status = 'Not Paid';

    if (!snapshot.empty) {
      batch.update(docRef, updateData);
      if (Math.abs(currentBalance - newBalance) > 0.001) {
        this.addActivityToBatch(batch, docRef, {
          category: 'Balance Update',
          fieldChanged: 'Beginning Balance',
          oldValue: currentBalance,
          newValue: newBalance,
          activityBy: userName
        });
      }
      if (oldStatus !== updateData.status) {
        this.addActivityToBatch(batch, docRef, {
          category: 'Invoice Status Change',
          fieldChanged: 'Status',
          oldValue: oldStatus,
          newValue: updateData.status,
          activityBy: userName
        });
      }
    } else {
      batch.set(docRef, {
        ...updateData,
        companyId: companyId,
        period: currentPeriod,
        emailStatus: 'Not Sent',
        createdAt: new Date(),
        createdBy: userName
      });
      this.addActivityToBatch(batch, docRef, {
        category: 'Balance Update',
        fieldChanged: 'Beginning Balance',
        oldValue: 0,
        newValue: newBalance,
        activityBy: userName
      });
    }

    await batch.commit();
    await this.recalculateLedgerForward(companyId, currentPeriod);
  }

  async updateInvoiceStatus(row: any, newStatus: string, userName: string): Promise<{ delta: number, feeCollectionTransfer: boolean, feeCollectionCompanyId?: string, transferredAmount?: number, transferredAmountUSD?: number, feeDelta?: number, feeDeltaUSD?: number, newAggregation?: any }> {
    const batch = writeBatch(this.firestore);
    const invoicesCol = collection(this.firestore, 'invoices');
    const q = query(invoicesCol, where('period', '==', row.period), where('companyId', '==', row.company.id));
    const snapshot = await getDocs(q);
    
    let oldBalance = row.remainingBalance;
    let oldStatus = row.status || 'Not Paid';
    let oldPaid = row.amountPaid || 0;

    if (!snapshot.empty) {
      const d = snapshot.docs[0].data();
      if (d['remainingBalance'] !== undefined) {
        oldBalance = d['remainingBalance'];
      }
      oldStatus = d['status'] || 'Not Paid';
      oldPaid = d['amountPaid'] || 0;
    }

    const originalAmountPaid = oldPaid;

    let newBalance = row.remainingBalance;
    let newAmountPaid = row.amountPaid;

    let earliestRecalcPeriod = row.period;
    if (newStatus === 'Paid') {
      // Overpayment guard: if the user explicitly entered an amount greater than totalDue,
      // preserve the actual amount paid and let remainingBalance go negative (credit carryover).
      // Only snap to totalDue / 0 for a standard "Paid" button click (exact payment).
      const displayPaid = row.displayAmountPaid !== undefined ? row.displayAmountPaid : row.totalDue;
      if (displayPaid > row.totalDue + 0.01) {
        newAmountPaid = displayPaid;
        newBalance = Math.round((row.totalDue - newAmountPaid) * 100) / 100; // negative → credit
      } else {
        newAmountPaid = row.totalDue;
        newBalance = 0;
      }
    } else if (newStatus === 'Not Paid') {
      newBalance = Math.round(row.totalDue * 100) / 100;
      newAmountPaid = 0;

      // D-2: Revert appliedFrom on prior invoices if this invoice had distributed funds to them
      const companyId = row.company?.id || row.companyId;
      if (companyId) {
        const priorInvoicesQuery = query(invoicesCol, where('companyId', '==', companyId));
        const priorInvoicesSnap = await getDocs(priorInvoicesQuery);
        for (const priorDoc of priorInvoicesSnap.docs) {
          if (!snapshot.empty && priorDoc.id === snapshot.docs[0].id) continue;
          const pData = priorDoc.data();
          const pPeriod = pData['period'];
          if (pPeriod >= row.period) continue;
          const appliedFromList = ((pData['appliedFrom'] || []) as Array<{ period: string; amount: number }>).slice();
          const appliedIndex = appliedFromList.findIndex(a => a.period === row.period);
          if (appliedIndex !== -1) {
            const removedAmount = appliedFromList[appliedIndex].amount;
            appliedFromList.splice(appliedIndex, 1);
            const pOldPaid = Number(pData['amountPaid'] || 0);
            const pNewPaid = Math.max(0, Math.round((pOldPaid - removedAmount) * 100) / 100);
            const pTotalDue = Number(pData['totalDue'] || 0);
            const pNewRem = Math.round((pTotalDue - pNewPaid) * 100) / 100;
            let pStatus = 'Not Paid';
            if (pNewRem <= 0.01) pStatus = 'Paid';
            else if (pNewPaid > 0.01) pStatus = 'Partial';

            batch.update(priorDoc.ref, {
              appliedFrom: appliedFromList,
              amountPaid: pNewPaid,
              remainingBalance: pNewRem,
              status: pStatus,
              updatedAt: new Date(),
              updatedBy: userName,
            });
            if (pPeriod < earliestRecalcPeriod) {
              earliestRecalcPeriod = pPeriod;
            }
          }
        }
      }
    }

    let feeCollectionTransfer = false, feeCollectionCompanyId, transferredAmount, transferredAmountUSD, feeDelta, feeDeltaUSD, newAggregation;
    const paidDelta = newAmountPaid - originalAmountPaid;

    if (paidDelta !== 0) {
      const transferResult = await this.transferToFeeCollection(row, newAmountPaid, paidDelta);
      if (transferResult) {
        feeCollectionTransfer = true;
        feeCollectionCompanyId = transferResult.feeCollectionCompanyId;
        transferredAmount = transferResult.transferredAmount;
        transferredAmountUSD = transferResult.transferredAmountUSD;
        feeDelta = transferResult.feeDelta;
        feeDeltaUSD = transferResult.feeDeltaUSD;
        newAggregation = transferResult.newAggregation;
      }
    }

    const updateData: any = { 
      status: newStatus, 
      remainingBalance: newBalance, 
      amountPaid: newAmountPaid,
      updatedAt: new Date(),
      updatedBy: userName
    };
    if (newStatus === 'Not Paid') {
      updateData.actualAmountPaid = 0;
    } else if (row.actualAmountPaid !== undefined) {
      updateData.actualAmountPaid = row.actualAmountPaid;
    }

    if (!snapshot.empty) {
      const docRef = snapshot.docs[0].ref;
      batch.set(docRef, updateData, { merge: true });
      if (oldStatus !== newStatus) {
        this.addActivityToBatch(batch, docRef, {
          category: 'Invoice Status Change',
          fieldChanged: 'Status',
          oldValue: oldStatus,
          newValue: newStatus,
          activityBy: userName
        });
      }
      if (Math.abs(oldBalance - newBalance) > 0.001) {
        this.addActivityToBatch(batch, docRef, {
          category: 'Balance Update',
          fieldChanged: 'Remaining Balance',
          oldValue: oldBalance,
          newValue: newBalance,
          activityBy: userName
        });
      }
    } else {
      const newInvoice: any = {
        companyId: row.company.id,
        period: row.period,
        status: newStatus,
        emailStatus: 'Not Sent',
        beginningBalance: row.beginningBalance ?? 0,
        totalFee: row.totalFee ?? 0,
        totalDue: row.totalDue ?? 0,
        totalRevenue: row.totalRevenue ?? 0,
        totalLfcFee: row.totalLfcFee ?? 0,
        totalKcegFee: row.totalKcegFee ?? 0,
        remainingBalance: newBalance,
        amountPaid: newAmountPaid,
        createdAt: new Date(),
        createdBy: userName
      };
      const newDocRef = doc(invoicesCol);
      batch.set(newDocRef, newInvoice);
      this.addActivityToBatch(batch, newDocRef, {
        category: 'Invoice Status Change',
        fieldChanged: 'Status',
        oldValue: 'Not Paid',
        newValue: newStatus,
        activityBy: userName
      });
    }
    
    const delta = newBalance - oldBalance;
    await batch.commit();
    const companyIdToRecalc = row.company?.id || row.companyId;
    await this.recalculateLedgerForward(companyIdToRecalc, earliestRecalcPeriod);
    return { delta, feeCollectionTransfer, feeCollectionCompanyId, transferredAmount, transferredAmountUSD, feeDelta, feeDeltaUSD, newAggregation };
  }

  async toggleUnlockInvoice(companyId: string, period: string, isUnlocked: boolean, userName: string, docId?: string, companyName?: string): Promise<string | undefined> {
    const invoicesCol = collection(this.firestore, 'invoices');
    const updateData: any = {
      isManuallyUnlocked: isUnlocked,
      unlockedAt: isUnlocked ? new Date() : null,
      unlockedBy: isUnlocked ? userName : null,
      updatedAt: new Date(),
      updatedBy: userName
    };

    let targetDocId = docId;

    // 1. Direct docId update if provided
    if (docId) {
      try {
        const directRef = doc(this.firestore, 'invoices', docId);
        await setDoc(directRef, updateData, { merge: true });
        targetDocId = docId;
      } catch (err) {
        console.warn('Direct docId update failed in toggleUnlockInvoice:', err);
      }
    }

    // 2. Query all invoices for this period and update any matching by companyId or company name
    if (period) {
      const q = query(invoicesCol, where('period', '==', period));
      const snap = await getDocs(q);
      let matched = false;
      for (const docSnap of snap.docs) {
        const d = docSnap.data();
        const docCid = d['companyId'] || d['company']?.id || (typeof d['company'] === 'string' ? d['company'] : '');
        const docCName = (d['company'] && typeof d['company'] === 'object' ? d['company'].company : '') || d['companyName'] || '';
        
        if (
          (companyId && (docCid === companyId || docSnap.id === companyId)) ||
          (companyName && (docCName === companyName || docCid === companyName))
        ) {
          await setDoc(docSnap.ref, updateData, { merge: true });
          matched = true;
          if (!targetDocId) targetDocId = docSnap.id;
        }
      }

      // 3. Fallback: if no document existed at all, create one
      if (!matched && !targetDocId && companyId) {
        const newRef = await addDoc(invoicesCol, {
          companyId,
          period,
          ...updateData,
          createdAt: new Date(),
          createdBy: userName,
          status: 'Not Paid',
          emailStatus: 'Not Sent'
        });
        targetDocId = newRef.id;
      }
    }

    return targetDocId;
  }

  async toggleCancelStatus(row: any, userName: string): Promise<{ status: string, delta: number } | null> {
    const batch = writeBatch(this.firestore);
    const invoicesCol = collection(this.firestore, 'invoices');
    const q = query(invoicesCol, where('period', '==', row.period), where('companyId', '==', row.company.id));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    
    const docRef = snapshot.docs[0].ref;
    const data = snapshot.docs[0].data();
    const currentStatus = data['status'];
    const currentBalance = Number(data['remainingBalance'] || 0);
    const currentPaid = Number(data['amountPaid'] || 0);
    
    let newStatus = '';
    let delta = 0;

    if (currentStatus === 'Canceled') {
      if (currentBalance <= 0.01) newStatus = 'Paid';
      else if ((row.totalDue - currentBalance) > 0.01) newStatus = 'Partial';
      else newStatus = 'Not Paid';
      delta = currentBalance;
    } else {
      newStatus = 'Canceled';
      delta = -currentBalance;
    }

    batch.update(docRef, { 
      status: newStatus,
      updatedAt: new Date(),
      updatedBy: userName
    });
    
    this.addActivityToBatch(batch, docRef, {
      category: 'Invoice Status Change',
      fieldChanged: 'Status',
      oldValue: currentStatus,
      newValue: newStatus,
      activityBy: userName
    });

    await batch.commit();
    await this.recalculateLedgerForward(row.company.id, row.period);
    return { status: newStatus, delta };
  }

  async applyPaymentDistribution(company: any, changes: any[], userName: string, currentPeriod: string, actualAmountPaid: number) {
    const batch = writeBatch(this.firestore);
    const invoicesCol = collection(this.firestore, 'invoices');
    
    let lastDelta = 0;
    let currentPeriodInChanges = false;
    
    for (const change of changes) {
        if (change.period === currentPeriod) currentPeriodInChanges = true;

        const q = query(invoicesCol, where('companyId', '==', company.id), where('period', '==', change.period));
        const snap = await getDocs(q);
        
        const existingData = !snap.empty ? snap.docs[0].data() : {};
        
        let newStatus = 'Not Paid';
        if (change.newRem <= 0.01) newStatus = 'Paid';
        else if (change.newPaid > 0.01) newStatus = 'Partial';
        
        const updateData: any = {
           amountPaid: change.newPaid,
           remainingBalance: change.newRem,
           totalDue: change.totalDue,
           beginningBalance: change.begBal,
           status: newStatus,
           updatedAt: new Date(),
           updatedBy: userName,
           isEdited: true
        };
        

        
        const paidDelta = change.newPaid - change.oldPaid;
        
        if (change.period === currentPeriod) {
            updateData.actualAmountPaid = actualAmountPaid;
        } else if (paidDelta !== 0) {
            let existingAppliedFrom = (existingData['appliedFrom'] || []) as {period: string, amount: number}[];
            if (paidDelta > 0) {
                const existing = existingAppliedFrom.find((x: any) => x.period === currentPeriod);
                if (existing) {
                    existing.amount += paidDelta;
                } else {
                    existingAppliedFrom.push({ period: currentPeriod, amount: paidDelta });
                }
            } else {
                let remainingToSubtract = -paidDelta;
                for (let i = existingAppliedFrom.length - 1; i >= 0; i--) {
                    if (remainingToSubtract <= 0) break;
                    if (existingAppliedFrom[i].period === currentPeriod) {
                        if (existingAppliedFrom[i].amount > remainingToSubtract) {
                            existingAppliedFrom[i].amount -= remainingToSubtract;
                            remainingToSubtract = 0;
                        } else {
                            remainingToSubtract -= existingAppliedFrom[i].amount;
                            existingAppliedFrom.splice(i, 1);
                        }
                    }
                }
                for (let i = existingAppliedFrom.length - 1; i >= 0; i--) {
                    if (remainingToSubtract <= 0) break;
                    if (existingAppliedFrom[i].amount > remainingToSubtract) {
                        existingAppliedFrom[i].amount -= remainingToSubtract;
                        remainingToSubtract = 0;
                    } else {
                        remainingToSubtract -= existingAppliedFrom[i].amount;
                        existingAppliedFrom.splice(i, 1);
                    }
                }
            }
            updateData.appliedFrom = existingAppliedFrom.filter(x => x.amount > 0.01);
        }
        
        if (!snap.empty) {
           const docRef = snap.docs[0].ref;
           batch.update(docRef, updateData);
           this.addActivityToBatch(batch, docRef, {
              category: 'Payment Distribution',
              fieldChanged: 'Amount Paid',
              oldValue: change.oldPaid,
              newValue: change.newPaid,
              activityBy: userName
           });
           lastDelta = change.newRem - (existingData['remainingBalance'] || 0);
        } else {
           const newDocRef = doc(invoicesCol);
           const newData = {
               companyId: company.id,
               company: company,
               period: change.period,
               ...updateData,
               createdAt: new Date(),
               createdBy: userName,
               emailStatus: 'Not Sent'
           };
           batch.set(newDocRef, newData);
           this.addActivityToBatch(batch, newDocRef, {
              category: 'Invoice Creation',
              fieldChanged: 'Creation via Distribution',
              oldValue: 0,
              newValue: change.newPaid,
              activityBy: userName
           });
           lastDelta = change.newRem;
        }
    }

    if (!currentPeriodInChanges) {
        const q = query(invoicesCol, where('companyId', '==', company.id), where('period', '==', currentPeriod));
        const snap = await getDocs(q);
        if (!snap.empty) {
            batch.update(snap.docs[0].ref, {
                actualAmountPaid: actualAmountPaid,
                updatedAt: new Date(),
                updatedBy: userName,
                isEdited: true
            });
        }
    }

    await batch.commit();

    if (changes.length > 0) {
        const sortedChanges = [...changes].sort((a, b) => a.period.localeCompare(b.period));
        const earliestPeriod = sortedChanges[0].period;
        await this.recalculateLedgerForward(company.id, earliestPeriod);
    }

    // Now MM transfers
    for (const change of changes) {
        const paidDelta = change.newPaid - change.oldPaid;
        if (paidDelta !== 0) {
            const invoiceRow = {
                company: company,
                period: change.period,
                totalDue: change.totalDue,
                totalLfcFee: change.totalLfcFee,
                totalKcegFee: change.totalKcegFee,
                exchangeRate: change.exchangeRate
            };
            await this.transferToFeeCollection(invoiceRow, change.newPaid, paidDelta);
        }
    }
  }

  async updateRemainingBalance(row: any, userName: string): Promise<{ delta: number, feeCollectionTransfer: boolean, feeCollectionCompanyId?: string, transferredAmount?: number, transferredAmountUSD?: number, feeDelta?: number, feeDeltaUSD?: number, newAggregation?: any }> {
    const batch = writeBatch(this.firestore);
    const invoicesCol = collection(this.firestore, 'invoices');
    const q = query(invoicesCol, where('period', '==', row.period), where('companyId', '==', row.company.id));
    const snapshot = await getDocs(q);

    let oldBalance = row.totalDue;
    let oldStatus = 'Not Paid';
    let oldPaid = 0;
    let docRef;

    if (!snapshot.empty) {
      const d = snapshot.docs[0].data();
      if (d['remainingBalance'] !== undefined) {
        oldBalance = d['remainingBalance'];
      }
      oldStatus = d['status'] || 'Not Paid';
      oldPaid = d['amountPaid'] || 0;
      docRef = snapshot.docs[0].ref;
    } else {
      docRef = doc(invoicesCol);
    }
    
    let feeCollectionTransfer = false, feeCollectionCompanyId, transferredAmount, transferredAmountUSD, feeDelta, feeDeltaUSD, newAggregation;
    const paidDelta = row.amountPaid - oldPaid;
    if (paidDelta !== 0) {
      const transferResult = await this.transferToFeeCollection(row, row.amountPaid, paidDelta);
      if (transferResult) {
        feeCollectionTransfer = true;
        feeCollectionCompanyId = transferResult.feeCollectionCompanyId;
        transferredAmount = transferResult.transferredAmount;
        transferredAmountUSD = transferResult.transferredAmountUSD;
        feeDelta = transferResult.feeDelta;
        feeDeltaUSD = transferResult.feeDeltaUSD;
        newAggregation = transferResult.newAggregation;
      }
    }

    const updateData: any = { 
      remainingBalance: Math.round(row.remainingBalance * 100) / 100, 
      amountPaid: row.amountPaid,
      isEdited: true,
      updatedAt: new Date(),
      updatedBy: userName
    };
    if (row.actualAmountPaid !== undefined) {
      updateData.actualAmountPaid = row.actualAmountPaid;
    }

    if (row.remainingBalance <= 0.01) {
      updateData.status = 'Paid';
    } else if (row.amountPaid > 0.01) {
      updateData.status = 'Partial';
    } else {
      updateData.status = 'Not Paid';
    }

    if (!snapshot.empty) {
      batch.set(docRef, updateData, { merge: true });
      if (Math.abs(oldPaid - row.amountPaid) > 0.001) {
        this.addActivityToBatch(batch, docRef, {
          category: 'Payment Update',
          fieldChanged: 'Amount Paid',
          oldValue: oldPaid,
          newValue: row.amountPaid,
          activityBy: userName
        });
      }
      if (oldStatus !== updateData.status) {
        this.addActivityToBatch(batch, docRef, {
          category: 'Invoice Status Change',
          fieldChanged: 'Status',
          oldValue: oldStatus,
          newValue: updateData.status,
          activityBy: userName
        });
      }
    } else {
      const newInvoice: any = {
        companyId: row.company.id,
        period: row.period,
        status: updateData.status || 'Not Paid',
        emailStatus: 'Not Sent',
        beginningBalance: row.beginningBalance ?? 0,
        totalFee: row.totalFee ?? 0,
        totalDue: row.totalDue ?? 0,
        totalRevenue: row.totalRevenue ?? 0,
        totalLfcFee: row.totalLfcFee ?? 0,
        totalKcegFee: row.totalKcegFee ?? 0,
        remainingBalance: row.remainingBalance,
        amountPaid: row.amountPaid,
        createdAt: new Date(),
        createdBy: userName,
        isEdited: true
      };
      batch.set(docRef, newInvoice);
      this.addActivityToBatch(batch, docRef, {
        category: 'Payment Update',
        fieldChanged: 'Amount Paid',
        oldValue: 0,
        newValue: row.amountPaid,
        activityBy: userName
      });
    }

    const delta = row.remainingBalance - oldBalance;
    await batch.commit();
    await this.recalculateLedgerForward(row.company.id, row.period);
    return { delta, feeCollectionTransfer, feeCollectionCompanyId, transferredAmount, transferredAmountUSD, feeDelta, feeDeltaUSD, newAggregation };
  }

  async createNewStores(stores: any[], userName: string) {
    return this.storeService.createNewStores(stores, userName);
  }

  async updateStores(stores: any[], userName: string): Promise<void> {
    const batch = writeBatch(this.firestore);
    
    stores.forEach(store => {
      if (store.id) {
        const storeRef = doc(this.firestore, 'stores', store.id);
        batch.update(storeRef, { 
          country: store.country,
          updatedAt: new Date(),
          updatedBy: userName
        });
      }
    });

    if (stores.length > 0) {
      await batch.commit();
    }
  }

  async deletePreviousImport(importId: string, storeToCompanyMap: Map<string, string[]>, rates: SmrsRate[], period: string): Promise<Map<string, number>> {
    const companyDeltas = new Map<string, number>();
    const aggCollection = collection(this.firestore, 'monthly_store_aggregations');
    const q = query(aggCollection, where('importId', '==', importId));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      querySnapshot.forEach((doc) => {
        const d = doc.data();
        const sid = String(d['storeId']).trim();
        const normSid = sid.toLowerCase().replace(/[^a-z0-9]/g, '');
        const cids = storeToCompanyMap.get(sid) || storeToCompanyMap.get(normSid) || [];
        
        let fee = 0;
        if (d['revenues']) {
          rates.forEach(r => {
            const rateRevenue = this.calculateRevenueForRate(d, r);
            fee += rateRevenue * (r.rate / 100);
          });
        }
        
        cids.forEach(cid => {
          const current = companyDeltas.get(cid) || 0;
          companyDeltas.set(cid, current - fee);
        });
      });

      const deleteBatch = writeBatch(this.firestore);
      querySnapshot.forEach((doc) => deleteBatch.delete(doc.ref));
      await deleteBatch.commit();
    }
    return companyDeltas;
  }

  async deleteHistory(historyId: string) {
    await deleteDoc(doc(this.firestore, 'invoice_upload_history', historyId));
  }

  async saveImportData(
    aggregations: any, 
    historyData: any, 
    period: string, 
    importId: string, 
    userName: string,
    storeToCompanyMap: Map<string, string[]>,
    rates: SmrsRate[]
  ): Promise<Map<string, number>> {
    const batch = writeBatch(this.firestore);
    const periodKey = period.replace('/', '-');
    const companyDeltas = new Map<string, number>();

    for (const [storeId, data] of Object.entries(aggregations)) {
      const aggData = data as any;
      const docId = `${periodKey}-${storeId}-${importId}`;
      const aggRef = doc(this.firestore, 'monthly_store_aggregations', docId);
      
      batch.set(aggRef, {
        ...aggData,
        storeId,
        period: period,
        importId: importId,
        sourceFileUrl: historyData.downloadURL,
        updatedAt: new Date(),
        updatedBy: userName
      });

      // Calculate Delta
      const sid = String(storeId).trim();
      const normSid = sid.toLowerCase().replace(/[^a-z0-9]/g, '');
      const cids = storeToCompanyMap.get(sid) || storeToCompanyMap.get(normSid) || [];
      let fee = 0;
      if (aggData.revenues) {
        rates.forEach(r => {
          const rateRevenue = this.calculateRevenueForRate(aggData, r);
          fee += rateRevenue * (r.rate / 100);
        });
      }
      cids.forEach(cid => {
        const current = companyDeltas.get(cid) || 0;
        companyDeltas.set(cid, current + fee);
      });
    }

    const historyRef = doc(collection(this.firestore, 'invoice_upload_history'));
    batch.set(historyRef, historyData, { merge: true });

    await batch.commit();
    return companyDeltas;
  }

  async processInvoiceUpdates(
    period: string, 
    companyDeltas: Map<string, number>, 
    importId: string | undefined, 
    stores: any[], 
    rates: SmrsRate[], 
    userName: string,
    onProgress?: (status: string) => void
  ) {
    const batch = writeBatch(this.firestore);
    const invoicesCol = collection(this.firestore, 'invoices');
    
    const qInv = query(invoicesCol, where('period', '==', period));
    const invSnap = await getDocs(qInv);
    const invoiceMap = new Map<string, any>();
    invSnap.forEach(doc => invoiceMap.set(doc.data()['companyId'], doc));

    let aggDocs: any[] | null = null;
    const affectedCompanyIds = new Set<string>();

    for (const [companyId, delta] of companyDeltas.entries()) {
      affectedCompanyIds.add(companyId);
      if (invoiceMap.has(companyId)) {
        if (Math.abs(delta) < 0.001) continue;
        const docSnap = invoiceMap.get(companyId);
        const docRef = docSnap.ref;
        const data = docSnap.data();

        if (!aggDocs) {
          const aggRef = collection(this.firestore, 'monthly_store_aggregations');
          const qAgg = query(aggRef, where('period', '==', period));
          const aggSnap = await getDocs(qAgg);
          aggDocs = aggSnap.docs.map(d => d.data());
        }

        const companyStores = stores.filter(s => {
          const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
          return cids.includes(companyId);
        }).map(s => String(s['storeId']).trim());
        
        let totalRevenue = 0;
        let totalFee = 0;
        let totalLfcFee = 0;
        let totalKcegFee = 0;

        aggDocs!.forEach((d: any) => {
          const sid = String(d['storeId']).trim();
          const cid = d['companyId'];
          if (companyStores.includes(sid) || (cid && cid === companyId)) {
            if (d.revenues) {
              rates.forEach(r => {
                const rateRevenue = this.calculateRevenueForRate(d, r);
                totalRevenue += rateRevenue;
                const fee = rateRevenue * (r.rate / 100);
                totalFee += fee;

                const kcegRateVal = (r as any).kcegRate !== undefined && (r as any).kcegRate !== null ? Number((r as any).kcegRate) : 0;
                const lfcRateVal = (r.rate || 0) - kcegRateVal;
                if ((r.rate || 0) !== 0) {
                  totalKcegFee += fee * (kcegRateVal / r.rate);
                  totalLfcFee += fee * (lfcRateVal / r.rate);
                }
              });
            }
          }
        });

        const begBal = Number(data['beginningBalance'] || 0);
        const amtPaid = Number(data['amountPaid'] || 0);
        const oldBalance = Number(data['remainingBalance'] || 0);
        const newTotalDue = Math.round((begBal + totalFee) * 100) / 100;
        const newBalance = Math.round((newTotalDue - amtPaid) * 100) / 100;
        
        let status = data['status'];
        if (newBalance <= 0.01) status = 'Paid';
        else if (amtPaid > 0.01) status = 'Partial';
        else status = 'Not Paid';
        
        batch.update(docRef, {
          totalRevenue: Math.round(totalRevenue * 100) / 100,
          totalFee: Math.round(totalFee * 100) / 100,
          totalLfcFee: Math.round(totalLfcFee * 100) / 100,
          totalKcegFee: Math.round(totalKcegFee * 100) / 100,
          totalDue: newTotalDue,
          remainingBalance: newBalance,
          status: status,
          updatedAt: new Date(),
          updatedBy: userName,
          ...(importId ? { importIds: arrayUnion(importId) } : {})
        });

        this.addActivityToBatch(batch, docRef, {
          category: 'Revenue Change',
          fieldChanged: 'Remaining Balance',
          oldValue: oldBalance,
          newValue: newBalance,
          activityBy: userName
        });
      } else {
        if (!aggDocs) {
             const aggRef = collection(this.firestore, 'monthly_store_aggregations');
             const qAgg = query(aggRef, where('period', '==', period));
             const aggSnap = await getDocs(qAgg);
             aggDocs = aggSnap.docs.map(d => d.data());
        }

        const companyStores = stores.filter(s => {
             const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
             return cids.includes(companyId);
        }).map(s => String(s['storeId']).trim());
        
        let totalRevenue = 0;
        let totalFee = 0;
        let totalLfcFee = 0;
        let totalKcegFee = 0;

        aggDocs!.forEach((d: any) => {
             if (companyStores.includes(String(d['storeId']).trim())) {
                if (d.revenues) {
                  rates.forEach(r => {
                    const rateRevenue = this.calculateRevenueForRate(d, r);
                    totalRevenue += rateRevenue;
                    const fee = rateRevenue * (r.rate / 100);
                    totalFee += fee;

                    const kcegRateVal = (r as any).kcegRate !== undefined && (r as any).kcegRate !== null ? Number((r as any).kcegRate) : 0;
                    const lfcRateVal = (r.rate || 0) - kcegRateVal;
                    if ((r.rate || 0) !== 0) {
                      totalKcegFee += fee * (kcegRateVal / r.rate);
                      totalLfcFee += fee * (lfcRateVal / r.rate);
                    }
                  });
                }
             }
        });

        const beginningBalance = await this.getBeginningBalance(companyId, period);
        const newBalance = Math.round((beginningBalance + totalFee) * 100) / 100;
        
        const newInvoice: any = {
          companyId: companyId,
          period: period,
          status: 'Not Paid',
          emailStatus: 'Not Sent',
          beginningBalance: beginningBalance,
          totalFee: Math.round(totalFee * 100) / 100,
          totalRevenue: Math.round(totalRevenue * 100) / 100,
          totalLfcFee: Math.round(totalLfcFee * 100) / 100,
          totalKcegFee: Math.round(totalKcegFee * 100) / 100,
          totalDue: newBalance,
          remainingBalance: newBalance,
          amountPaid: 0,
          createdAt: new Date(),
          createdBy: userName,
          updatedAt: new Date(),
          updatedBy: userName,
          ...(importId ? { importIds: [importId] } : {})
        };
        
        const newDocRef = doc(invoicesCol);
        batch.set(newDocRef, newInvoice);

        this.addActivityToBatch(batch, newDocRef, {
          category: 'Revenue Change',
          fieldChanged: 'Remaining Balance',
          oldValue: 0,
          newValue: newBalance,
          activityBy: userName
        });
      }
    }
    
    if (onProgress) onProgress('Updating invoice records in database...');
    await batch.commit();

    const companyIdArr = Array.from(affectedCompanyIds);
    for (let i = 0; i < companyIdArr.length; i++) {
      const cid = companyIdArr[i];
      if (onProgress) {
        onProgress(`Recalculating forward ledger balances (${i + 1}/${companyIdArr.length})...`);
      }
      // Do NOT pass single-period rates/stores — let recalculateLedgerForward fetch ALL rates from Firestore.
      await this.recalculateLedgerForward(cid, period);
    }
  }

  async cleanupImportData(importId: string) {
    try {
      const batch = writeBatch(this.firestore);
      let hasDeletions = false;

      const aggRef = collection(this.firestore, 'monthly_store_aggregations');
      const qAgg = query(aggRef, where('importId', '==', importId));
      const aggSnap = await getDocs(qAgg);
      aggSnap.forEach(doc => {
        batch.delete(doc.ref);
        hasDeletions = true;
      });

      const histRef = collection(this.firestore, 'invoice_upload_history');
      const qHist = query(histRef, where('importId', '==', importId));
      const histSnap = await getDocs(qHist);
      histSnap.forEach(doc => {
        batch.delete(doc.ref);
        hasDeletions = true;
      });

      if (hasDeletions) {
        await batch.commit();
      }
    } catch (error) {
      console.error('Error during cleanup:', error);
    }
  }

  async deleteInvoiceHistory(period: string): Promise<void> {
    const invoicesRef = collection(this.firestore, 'invoices');
    const q = query(invoicesRef, where('period', '==', period));
    const snapshot = await getDocs(q);
    const batch = writeBatch(this.firestore);
    snapshot.forEach(doc => batch.delete(doc.ref));
    await batch.commit();
  }

  async checkDuplicateImport(period: string, fileName: string, recordCount: number): Promise<any> {
    const historyCol = collection(this.firestore, 'invoice_upload_history');
    const q = query(historyCol, where('period', '==', period));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.find(d => {
      const data = d.data();
      const dbFileName = (data['fileName'] || '').normalize('NFC').trim().toLowerCase();
      const uploadFileName = fileName.normalize('NFC').trim().toLowerCase();
      const dbCount = Number(data['recordCount']);
      return dbFileName === uploadFileName && dbCount === recordCount;
    });
  }

  public calculateRevenueForRate(d: any, rate: SmrsRate): number {
    let val = 0;
    const revenues = d['revenues'] || {};
    const ips = d['ips'];

    rate.importKeys?.forEach(k => {
      if (revenues[k]) val += (Number(revenues[k]) || 0);
      
      if (ips) {
        Object.values(ips).forEach((ipMap: any) => {
          if (ipMap && ipMap[k]) {
            val += Number(ipMap[k]);
          }
        });
      }
    });

    if (rate.id && revenues[rate.id]) val += (Number(revenues[rate.id]) || 0);
    return val;
  }

  calculateRevenueAndFees(aggregations: any[], stores: any[], rates: SmrsRate[], period: string): Map<string, { revenue: number, fee: number, lfcFee: number, kcegFee: number }> {
    const storeToCompanyMap = new Map<string, string[]>();
    stores.forEach(s => {
      const sid = String(s['storeId'] || '').trim();
      if (!sid) return;
      const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
      storeToCompanyMap.set(sid, cids);
    });

    const ratesByPeriod = new Map<string | undefined, SmrsRate[]>();
    const isYear = /^\d{4}$/.test(period);

    if (period && period !== 'All Periods' && !isYear) {
      ratesByPeriod.set(period, rates.filter(r => r.period === period));
    } else if (isYear) {
      rates.forEach(r => {
        if (r.period && r.period.startsWith(period)) {
          if (!ratesByPeriod.has(r.period)) ratesByPeriod.set(r.period, []);
          ratesByPeriod.get(r.period)!.push(r);
        }
      });
    } else {
      rates.forEach(r => {
        if (!ratesByPeriod.has(r.period)) ratesByPeriod.set(r.period, []);
        ratesByPeriod.get(r.period)!.push(r);
      });
    }

    const aggDataMap = new Map<string, { revenue: number, fee: number, lfcFee: number, kcegFee: number }>();

    aggregations.forEach((d: any) => {
      const sid = String(d['storeId'] || '').trim();
      const dPeriod = d['period'];

      if (period !== 'All Periods') {
        if (isYear) {
          if (!dPeriod || !dPeriod.startsWith(period)) return;
        } else {
          if (dPeriod !== period) return;
        }
      }

      let companyIds = storeToCompanyMap.get(sid) || [];
      
      if (d['companyId']) {
        companyIds = [d['companyId']];
      }

      if (companyIds.length === 0) return;

      let revenue = 0;
      let fee = 0;
      let lfcFee = 0;
      let kcegFee = 0;
      if (d['revenues']) {
        const currentRates = ratesByPeriod.get(dPeriod) || [];
        currentRates.forEach(r => {
          const val = this.calculateRevenueForRate(d, r);
          revenue += val;
          const currentFee = val * (r.rate / 100);
          fee += currentFee;

          const kcegRateVal = (r as any).kcegRate !== undefined && (r as any).kcegRate !== null ? Number((r as any).kcegRate) : 0;
          const lfcRateVal = (r.rate || 0) - kcegRateVal;

          if ((r.rate || 0) !== 0) {
            kcegFee += currentFee * (kcegRateVal / r.rate);
            lfcFee += currentFee * (lfcRateVal / r.rate);
          }
        });
      }

      companyIds.forEach(cid => {
        const key = `${dPeriod}_${cid}`;
        if (!aggDataMap.has(key)) {
          aggDataMap.set(key, { revenue: 0, fee: 0, lfcFee: 0, kcegFee: 0 });
        }
        const current = aggDataMap.get(key)!;
        current.revenue += revenue;
        current.fee += fee;
        current.lfcFee += lfcFee;
        current.kcegFee += kcegFee;
      });
    });

    return aggDataMap;
  }

  calculateCompanyRevenueBreakdown(aggregations: any[], companyStores: any[], rates: SmrsRate[], companyId?: string, isFeeCollection?: boolean): { breakdown: any[], totalRevenue: number, totalFee: number } {
    const storeIds = new Set<string>();
    companyStores.forEach(s => {
      const sid = String(s['storeId'] || '').trim();
      if (sid) storeIds.add(sid);
    });

    const revenueMap = new Map<string, number>();
    
    aggregations.forEach((d: any) => {
      const sid = String(d['storeId'] || '').trim();
      
      let belongsToCompany = false;
      if (d['companyId']) {
        belongsToCompany = companyId ? d['companyId'] === companyId : false;
      } else {
        belongsToCompany = storeIds.has(sid);
      }

      if (belongsToCompany && d['revenues'] && !d['isAdjustment']) {
        rates.forEach(r => {
          const val = this.calculateRevenueForRate(d, r);
          if (r.id) {
             revenueMap.set(r.id, (revenueMap.get(r.id) || 0) + val);
          }
        });
      }
    });

    const breakdown: any[] = [];
    let totalRevenue = 0;
    let totalFee = 0;

    rates.forEach(r => {
      if (r.id) {
        const rev = revenueMap.get(r.id) || 0;
        const fee = isFeeCollection ? 0 : rev * (r.rate / 100);
        breakdown.push({
          name: r.name,
          revenue: rev,
          rate: r.rate,
          fee: fee
        });
        totalRevenue += rev;
        totalFee += fee;
      }
    });

    return { breakdown, totalRevenue, totalFee };
  }

  async recalculateInvoicesForPeriod(period: string, rates: SmrsRate[], stores: any[]) {
    const aggDocs = await this.getAggregations(period);
    const revenueFeesMap = this.calculateRevenueAndFees(aggDocs, stores, rates, period);
    const invoices = await this.getInvoices(period);
    
    const batch = writeBatch(this.firestore);
    let hasUpdates = false;
    const changedCompanyIds = new Set<string>();

    for (const inv of invoices) {
       const companyId = inv['companyId'];
       const key = `${period}_${companyId}`;
       const agg = revenueFeesMap.get(key);
       const fee = agg ? Math.round(agg.fee * 100) / 100 : 0;
       
       let beginningBalance = 0;
       if (inv['beginningBalanceOverride'] !== undefined) {
         beginningBalance = inv['beginningBalanceOverride'];
       } else {
         beginningBalance = await this.getBeginningBalance(companyId, period);
       }
       
       const totalDue = Math.round((beginningBalance + fee) * 100) / 100;
       const amountPaid = inv['amountPaid'] || 0;
       const newRemaining = Math.round((totalDue - amountPaid) * 100) / 100;
       
       const oldRemaining = inv['remainingBalance'];
       
       if (Math.abs(newRemaining - oldRemaining) > 0.001) {
          if (inv['status'] === 'Canceled') continue;

          let status = inv['status'];
          if (newRemaining <= 0.01) status = 'Paid';
          else if (amountPaid > 0.01) status = 'Partial';
          else status = 'Not Paid';
          
          batch.update(inv['ref'], {
            beginningBalance: beginningBalance,
            totalFee: fee,
            totalDue: totalDue,
            remainingBalance: newRemaining,
            status: status,
            updatedAt: new Date()
          });
          
          hasUpdates = true;
          changedCompanyIds.add(companyId);
       }
    }
    
    if (hasUpdates) {
      await batch.commit();
      for (const cid of changedCompanyIds) {
        // Do NOT pass single-period rates/stores — let recalculateLedgerForward fetch ALL rates from Firestore.
        await this.recalculateLedgerForward(cid, period);
      }
    }
  }

  async getUserInvoicePreferences(userId: string): Promise<string[] | null> {
    const prefRef = doc(this.firestore, `users/${userId}/preferences/invoice`);
    const snap = await getDoc(prefRef);
    if (snap.exists()) {
      return snap.data()['companyIds'];
    }
    return null;
  }

  async saveUserInvoicePreferences(userId: string, companyIds: string[]): Promise<void> {
    const prefRef = doc(this.firestore, `users/${userId}/preferences/invoice`);
    await setDoc(prefRef, { companyIds }, { merge: true });
  }

  public async transferToFeeCollection(invoiceRow: any, newAmountPaid: number, paidDelta: number): Promise<{feeCollectionCompanyId: string, transferredAmount: number, transferredAmountUSD: number, feeDelta: number, feeDeltaUSD: number, newAggregation: any | null } | null> {
    if (!invoiceRow.company || !invoiceRow.company.brand || paidDelta === 0) {
      return null;
    }

    const companies = await firstValueFrom(this.getCompanies());
    const feeCollectionCompany = companies.find(c => c.brand === invoiceRow.company.brand && c.isFeeCollection);

    if (!feeCollectionCompany || feeCollectionCompany.id === invoiceRow.company.id) {
      return null;
    }

    const stores = await firstValueFrom(this.getStores());
    const companyCurrencyMap = new Map<string, string>();
    let fcStoreId = feeCollectionCompany.company; // fallback

    stores.forEach(s => {
      const currency = s['currency'] || (['Canada', '캐나다'].includes(s['country'] || '') ? 'CAD' : 'USD');
      const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
      cids.forEach((cid: string) => {
        if (currency !== 'USD') {
            companyCurrencyMap.set(cid, currency);
        } else if (!companyCurrencyMap.has(cid)) {
            companyCurrencyMap.set(cid, 'USD');
        }
        if (cid === feeCollectionCompany.id && s['storeId']) {
          fcStoreId = String(s['storeId']).trim();
        }
      });
    });

    // Delete existing transfers for this source company and period
    const aggRefCol = collection(this.firestore, 'monthly_store_aggregations');
    const qOld = query(aggRefCol, 
      where('period', '==', invoiceRow.period), 
      where('companyId', '==', feeCollectionCompany.id),
      where('sourceCompanyId', '==', invoiceRow.company.id)
    );
    
    const oldTransfersSnap = await getDocs(qOld);
    const deleteBatch = writeBatch(this.firestore);
    oldTransfersSnap.forEach(doc => {
      deleteBatch.delete(doc.ref);
    });
    if (!oldTransfersSnap.empty) {
      await deleteBatch.commit();
    }

    const exchangeRate = invoiceRow.exchangeRate || 1;
    
    const transferredAmount = paidDelta;
    const transferredAmountUSD = paidDelta * exchangeRate;
    const feeDelta = 0;
    const feeDeltaUSD = 0;

    if (newAmountPaid === 0) {
      return { feeCollectionCompanyId: feeCollectionCompany.id, transferredAmount, transferredAmountUSD, feeDelta, feeDeltaUSD, newAggregation: null };
    }

    let amountToRecord = newAmountPaid * exchangeRate;
    
    amountToRecord = Math.round(amountToRecord * 100) / 100;

    // Get rates to find a valid rate ID for this brand
    const ratesCol = collection(this.firestore, 'smrs_rates');
    const qRates = query(ratesCol, where('period', '==', invoiceRow.period));
    const ratesSnap = await getDocs(qRates);
    
    let targetRateId = 'FC_TRANSFER_RATE';
    const brandRates = ratesSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter((r: any) => (r.brand || '').trim() === feeCollectionCompany.brand);
      
    if (brandRates.length > 0) {
      brandRates.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
      targetRateId = brandRates[0].id;
    }

    const targetRate: any = brandRates.find(r => r.id === targetRateId);

    const ratio = invoiceRow.totalDue ? (newAmountPaid / invoiceRow.totalDue) : 0;
    const sourceLfcFee = (invoiceRow.totalLfcFee || 0) * ratio * exchangeRate;
    const sourceKcegFee = (invoiceRow.totalKcegFee || 0) * ratio * exchangeRate;

    const periodKey = invoiceRow.period.replace('/', '-');
    const importId = `fc_transfer_${Date.now()}`;
    const docId = `${periodKey}-${fcStoreId}-${importId}`;
    const aggRef = doc(this.firestore, 'monthly_store_aggregations', docId);
    
    const aggData = {
      storeId: fcStoreId,
      period: invoiceRow.period,
      revenues: { [targetRateId]: amountToRecord },
      importId: importId,
      companyId: feeCollectionCompany.id,
      sourceCompanyId: invoiceRow.company?.id || null,
      sourceCompanyName: invoiceRow.company?.company || null,
      sourceLfcFee: Math.round(sourceLfcFee * 100) / 100,
      sourceKcegFee: Math.round(sourceKcegFee * 100) / 100,
      isManual: true,
      updatedAt: new Date(),
      updatedBy: 'System Transfer'
    };

    await setDoc(aggRef, aggData);

    return { feeCollectionCompanyId: feeCollectionCompany.id, transferredAmount: amountToRecord, transferredAmountUSD, feeDelta, feeDeltaUSD, newAggregation: aggData };
  }

  public async deleteFeeCollectionTransfersForPeriod(period: string): Promise<number> {
    const companies = await firstValueFrom(this.getCompanies());
    const feeCollectionCompanyIds = companies.filter(c => c.isFeeCollection).map(c => c.id);

    if (feeCollectionCompanyIds.length === 0) {
      return 0;
    }

    const aggRefCol = collection(this.firestore, 'monthly_store_aggregations');
    const q = query(aggRefCol,
      where('period', '==', period),
      where('companyId', 'in', feeCollectionCompanyIds),
      where('isManual', '==', true)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return 0;
    }

    const batch = writeBatch(this.firestore);
    let deletedCount = 0;
    snapshot.forEach(doc => {
      if (doc.data()['sourceCompanyId']) {
        batch.delete(doc.ref);
        deletedCount++;
      }
    });
    
    if (deletedCount > 0) {
      await batch.commit();
    }
    return deletedCount;
  }

  public async deleteFeeCollectionTransfersForSourceCompany(sourceCompanyId: string, startPeriod: string, endPeriod: string): Promise<number> {
    const companies = await firstValueFrom(this.getCompanies());
    const feeCollectionCompanyIds = companies.filter(c => c.isFeeCollection).map(c => c.id);

    if (feeCollectionCompanyIds.length === 0) {
      return 0;
    }

    const aggRefCol = collection(this.firestore, 'monthly_store_aggregations');
    const q = query(aggRefCol,
      where('sourceCompanyId', '==', sourceCompanyId),
      where('isManual', '==', true)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return 0;
    }

    const batch = writeBatch(this.firestore);
    let deletedCount = 0;
    snapshot.forEach(doc => {
      const data = doc.data();
      const period = data['period'];
      if (period >= startPeriod && period <= endPeriod && feeCollectionCompanyIds.includes(data['companyId'])) {
        batch.delete(doc.ref);
        deletedCount++;
      }
    });

    if (deletedCount > 0) {
      await batch.commit();
    }
    return deletedCount;
  }

  /** Brand ownership of a device for invoice generation; the rule is shared with the Sales Report (device-brand.util.ts). */
  private deviceBelongsToBrand(dev: any, brandId: string | undefined, brandName: string): boolean {
    return deviceBelongsToBrand(dev, brandId, brandName);
  }

  /**
   * Read-only Generate preview (KAN-284). Classifies every company-period of the brand and computes the
   * regenerated values; nothing is written. `applyBrandGeneration` re-plans and compares fingerprints.
   */
  async planBrandGeneration(
    period: string,
    brandName: string,
    brandCompanies: any[],
    brandRates: SmrsRate[],
    allStores: any[],
    photoboothService: any,
    storeService: any,
    brandId?: string
  ): Promise<GenerationPlan> {
    const plan: GenerationPlan = { period, brandName, brandId, companies: [], fingerprint: '' };
    if (!period || period === 'All Periods' || !brandCompanies.length || !brandRates.length) {
      return plan;
    }

    const periodKey = period.replace('/', '-');
    const parts = period.split('/');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const endDate = new Date(year, month, 0);
    const pad = (n: number) => n.toString().padStart(2, '0');
    const startDateStr = `${year}-${pad(month)}-01`;
    const endDateStr = `${year}-${pad(month)}-${pad(endDate.getDate())}`;

    const [allDevicesData, aggSnap, invSnap] = await Promise.all([
      photoboothService.getAllDevicesDateRangeRevenue(startDateStr, endDateStr),
      getDocs(query(collection(this.firestore, 'monthly_store_aggregations'), where('period', '==', period))),
      getDocs(query(collection(this.firestore, 'invoices'), where('period', '==', period)))
    ]);
    const aggDocs = aggSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
    const existingInvoicesMap = new Map<string, any>();
    invSnap.forEach(d => existingInvoicesMap.set(d.data()['companyId'], { id: d.id, ...(d.data() as any) }));

    const activeStores = (allStores || []).filter((s: any) => s?.isDeleted !== true);

    for (const company of brandCompanies) {
      const companyId = company.id;
      const companyStores = activeStores.filter((s: any) => {
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        return cids.includes(companyId) || s['company'] === company.company;
      });
      const companyStoreIds = new Set<string>(companyStores.map((s: any) => String(s['storeId'] || s.id).trim()));
      const companyAggs = aggDocs.filter(a => aggregationBelongsToCompany(a, companyId, companyStoreIds, brandId));
      const existingInv = existingInvoicesMap.get(companyId) || null;
      const classification = classifyCompanyPeriod(existingInv, companyAggs);

      const before: GenerationSnapshot | null = existingInv ? {
        totalRevenue: Number(existingInv.totalRevenue ?? 0),
        totalFee: Number(existingInv.totalFee ?? 0),
        amountPaid: Number(existingInv.amountPaid || 0),
        remainingBalance: Number(existingInv.remainingBalance ?? 0),
        status: existingInv.status || 'Not Paid'
      } : null;

      const companyPlan: GenerationCompanyPlan = {
        companyId,
        companyName: company.company || company.name || companyId,
        classification,
        keptReason: classification === 'kept-canceled' ? 'Invoice is canceled'
          : classification === 'kept-manual' ? (existingInv?.isManual ? 'Manual invoice' : 'Has manual, import, adjustment or transfer entries') : undefined,
        existingInvoiceId: existingInv?.id || null,
        emailed: existingInv?.emailStatus === 'Sent',
        missingDevices: [],
        storeItems: [],
        deleteAggIds: [],
        before,
        after: null,
        beginningBalance: Number(existingInv?.beginningBalance ?? 0),
        totals: null,
        totalDue: 0,
        unchanged: false,
        forward: { affectedPeriods: [], latestRemainingChange: 0 }
      };
      plan.companies.push(companyPlan);
      if (classification === 'kept-manual' || classification === 'kept-canceled') continue;

      for (const store of companyStores) {
        const sid = String(store['storeId'] || store.id).trim();
        // A store can host devices of several brands; bill only this brand's devices.
        const storeDevices = (await storeService.getDevices(store.id || '', store.storeId))
          .filter((dev: any) => this.deviceBelongsToBrand(dev, brandId, brandName));

        let storeCardTotal = 0;
        let storeCashTotal = 0;
        for (const dev of storeDevices) {
          const devId = dev.deviceId || dev.id;
          const matchedDev = allDevicesData.find((d: any) => d.deviceId === devId);
          const hasDaily = Number(matchedDev?.reportDayCount || 0) > 0;
          let card = Number(matchedDev?.data?.CardTotal || matchedDev?.data?.cardTotal || matchedDev?.data?.credit || 0);
          let cash = Number(matchedDev?.data?.CashTotal || matchedDev?.data?.cashTotal || matchedDev?.data?.cash || 0);
          let hasMonthly = false;

          if (!card && !cash) {
            try {
              const monthlyRep = await photoboothService.getMonthlyReport(devId, periodKey) ||
                                 await photoboothService.getMonthlyReport(devId, period);
              if (monthlyRep) {
                hasMonthly = true;
                card = Number(monthlyRep['CardTotal'] || monthlyRep['cardTotal'] || monthlyRep['credit'] || 0);
                cash = Number(monthlyRep['CashTotal'] || monthlyRep['cashTotal'] || monthlyRep['cash'] || 0);
              }
            } catch (e) {
              // Treated as missing below
            }
          }

          // A missing report counts as $0 (partial refresh) and is named in the preview and results.
          if (!hasDaily && !hasMonthly) {
            companyPlan.missingDevices.push({
              deviceId: devId,
              deviceName: dev.deviceName || devId,
              storeId: sid,
              storeName: store.name || sid
            });
          }
          storeCardTotal += card;
          storeCashTotal += cash;
        }

        companyPlan.storeItems.push({
          storeId: sid,
          storeName: store.name || sid,
          revenues: this.assignRevenueToRates(brandRates, storeCardTotal, storeCashTotal)
        });
      }

      const generatedAggs = companyAggs.filter(a => isGeneratedAggregation(a));
      companyPlan.deleteAggIds = generatedAggs.map(a => a.id);

      const totals = computeGeneratedTotals(companyPlan.storeItems, brandRates);
      let beginningBalance = 0;
      if (company.isFeeCollection !== true) {
        beginningBalance = existingInv?.beginningBalanceOverride !== undefined
          ? Number(existingInv.beginningBalanceOverride)
          : await this.getBeginningBalance(companyId, period);
      }
      const amountPaid = Number(existingInv?.amountPaid || 0);
      const totalDue = Math.round((beginningBalance + totals.totalFee) * 100) / 100;
      const remainingBalance = Math.round((totalDue - amountPaid) * 100) / 100;
      const status = deriveStatus(remainingBalance, amountPaid);

      companyPlan.totals = totals;
      companyPlan.beginningBalance = beginningBalance;
      companyPlan.totalDue = totalDue;
      companyPlan.after = { totalRevenue: totals.totalRevenue, totalFee: totals.totalFee, amountPaid, remainingBalance, status };

      if (!existingInv) {
        // Nothing to bill: no revenue, no carried balance. No invoice is created and the company is not listed.
        companyPlan.unchanged = totals.totalRevenue === 0 && totals.totalFee === 0 && Math.abs(beginningBalance) < 0.005
          && generatedAggs.length === 0;
      } else {
        const same = (a: any, b: number) => Math.abs((Number(a) || 0) - b) < 0.005;
        companyPlan.unchanged = generatedRowsMatch(generatedAggs, companyPlan.storeItems)
          && same(existingInv.totalRevenue, totals.totalRevenue)
          && same(existingInv.totalFee, totals.totalFee)
          && same(existingInv.beginningBalance, beginningBalance)
          && same(existingInv.totalDue, totalDue)
          && same(existingInv.remainingBalance, remainingBalance)
          && existingInv.status === status;
      }

      if (!companyPlan.unchanged && company.isFeeCollection !== true) {
        const laterSnap = await getDocs(query(
          collection(this.firestore, 'invoices'),
          where('companyId', '==', companyId),
          where('period', '>', period)
        ));
        companyPlan.forward = simulateForwardBalances(remainingBalance, laterSnap.docs.map(d => d.data() as ForwardInvoice));
      }
    }

    plan.fingerprint = planFingerprint(plan.companies);
    return plan;
  }

  /**
   * Applies a confirmed Generate preview (KAN-284). Only the companies the preview will change are re-planned,
   * written and reported; kept and unchanged companies are counted from the preview, not read again. A changed
   * re-plan of those companies refuses the run as stale. Each company is written in its own batch: generated rows
   * replaced, the invoice refreshed in place (payment facts, email fields and overrides untouched), one audit
   * entry, then forward ledger recalculation.
   */
  async applyBrandGeneration(
    preview: GenerationPlan,
    brandCompanies: any[],
    brandRates: SmrsRate[],
    userName: string,
    allStores: any[],
    photoboothService: any,
    storeService: any
  ): Promise<GenerationResult> {
    const previewed = preview.companies.filter(c => willGenerate(c));
    const previewedIds = new Set(previewed.map(c => c.companyId));
    const plan = await this.planBrandGeneration(
      preview.period, preview.brandName, brandCompanies.filter(c => previewedIds.has(c.id)), brandRates,
      allStores, photoboothService, storeService, preview.brandId
    );
    if (planFingerprint(plan.companies) !== planFingerprint(previewed)) {
      throw new StaleGenerationPlanError();
    }
    const skipped = preview.companies.filter(c => !willGenerate(c));

    const period = plan.period;
    const periodKey = period.replace('/', '-');
    const runTs = Date.now();
    const generationRunId = `gen_${runTs}`;
    const startedAt = new Date();
    const outcomes: GenerationOutcome[] = [];

    for (const c of plan.companies) {
      const base = { companyId: c.companyId, companyName: c.companyName, partial: c.missingDevices.length > 0, missingDevices: c.missingDevices };
      try {
        const batch = writeBatch(this.firestore);
        c.deleteAggIds.forEach(id => batch.delete(doc(this.firestore, 'monthly_store_aggregations', id)));

        const importId = `generated_${runTs}_${c.companyId}`;
        for (const item of c.storeItems) {
          batch.set(doc(this.firestore, 'monthly_store_aggregations', `${periodKey}-${item.storeId}-${importId}`), {
            storeId: item.storeId,
            storeName: item.storeName,
            period: period,
            revenues: item.revenues,
            importId: importId,
            source: 'generated',
            generationRunId,
            companyId: c.companyId,
            brandId: plan.brandId || null,
            sourceFileUrl: null,
            updatedAt: new Date(),
            updatedBy: userName,
            isManual: false
          });
        }

        const after = c.after!;
        const totals = c.totals!;
        const computed = {
          beginningBalance: c.beginningBalance,
          totalRevenue: totals.totalRevenue,
          totalFee: totals.totalFee,
          totalLfcFee: totals.totalLfcFee,
          totalKcegFee: totals.totalKcegFee,
          totalDue: c.totalDue,
          remainingBalance: after.remainingBalance,
          status: after.status,
          lastGenerationRunId: generationRunId,
          updatedAt: new Date(),
          updatedBy: userName
        };

        let invRef;
        if (c.existingInvoiceId) {
          // Merge only computed fields: amountPaid, actualAmountPaid, appliedFrom, email fields,
          // beginningBalanceOverride, isManual and isEdited stay exactly as they are.
          invRef = doc(this.firestore, 'invoices', c.existingInvoiceId);
          batch.set(invRef, computed, { merge: true });
        } else {
          invRef = doc(collection(this.firestore, 'invoices'));
          batch.set(invRef, {
            ...computed,
            companyId: c.companyId,
            period: period,
            amountPaid: 0,
            emailStatus: 'Not Sent',
            emailSentAt: null,
            emailRecipients: '',
            isManual: false,
            isEdited: false,
            createdAt: new Date(),
            createdBy: userName
          });
        }
        this.addActivityToBatch(batch, invRef, {
          category: 'Invoice Auto-Generated',
          fieldChanged: c.existingInvoiceId ? 'Total Fee' : 'Created',
          oldValue: c.existingInvoiceId ? (c.before?.totalFee ?? null) : null,
          newValue: c.existingInvoiceId ? after.totalFee : 'Created',
          activityBy: userName,
          generationRunId,
          oldRemainingBalance: c.before?.remainingBalance ?? null,
          newRemainingBalance: after.remainingBalance
        });

        await batch.commit();
      } catch (err: any) {
        console.error(`Generate failed for company ${c.companyId}:`, err);
        outcomes.push({ ...base, outcome: 'failed', reason: err?.message || String(err) });
        continue;
      }

      try {
        // Do NOT pass single-period rates/stores — let recalculateLedgerForward fetch ALL rates from Firestore.
        await this.recalculateLedgerForward(c.companyId, period);
        outcomes.push({ ...base, outcome: c.existingInvoiceId ? 'refreshed' : 'created', reason: base.partial ? 'Missing device reports counted as $0' : '' });
      } catch (err: any) {
        console.error(`Ledger recalculation failed for company ${c.companyId}:`, err);
        outcomes.push({ ...base, outcome: 'failed', reason: `Invoice saved but forward ledger recalculation failed: ${err?.message || err}` });
      }
    }

    const counts = {
      created: outcomes.filter(o => o.outcome === 'created').length,
      refreshed: outcomes.filter(o => o.outcome === 'refreshed').length,
      unchanged: skipped.filter(c => c.unchanged).length,
      kept: skipped.filter(c => c.classification === 'kept-manual' || c.classification === 'kept-canceled').length,
      partial: outcomes.filter(o => o.partial && (o.outcome === 'created' || o.outcome === 'refreshed')).length,
      failed: outcomes.filter(o => o.outcome === 'failed').length
    };

    // A run that wrote nothing (all kept or unchanged) leaves no audit record.
    const wroteSomething = counts.created + counts.refreshed + counts.failed > 0;
    try {
      if (wroteSomething) await setDoc(doc(this.firestore, 'smrs_generation_runs', generationRunId), {
        generationRunId,
        brandId: plan.brandId || null,
        brandName: plan.brandName,
        period,
        actor: userName,
        startedAt,
        completedAt: new Date(),
        counts,
        companies: outcomes.map(o => ({
          companyId: o.companyId,
          companyName: o.companyName,
          outcome: o.outcome,
          reason: o.reason,
          missingDeviceIds: o.missingDevices.map(m => m.deviceId)
        }))
      });
    } catch (err) {
      console.error('Failed to record SMRS generation run:', err);
    }

    return { generationRunId, outcomes, counts };
  }

  /** Plan and apply in one step, without a preview. */
  async generateInvoicesForBrand(
    period: string,
    brandName: string,
    brandCompanies: any[],
    brandRates: SmrsRate[],
    userName: string,
    allStores: any[],
    photoboothService: any,
    storeService: any,
    brandId?: string
  ): Promise<{ generatedCount: number }> {
    const plan = await this.planBrandGeneration(period, brandName, brandCompanies, brandRates, allStores, photoboothService, storeService, brandId);
    if (!plan.companies.length) return { generatedCount: 0 };
    const result = await this.applyBrandGeneration(plan, brandCompanies, brandRates, userName, allStores, photoboothService, storeService);
    return { generatedCount: result.counts.created + result.counts.refreshed + result.counts.unchanged };
  }

  private assignRevenueToRates(brandRates: SmrsRate[], cardTotal: number, cashTotal: number): { [key: string]: number } {
    const revenues: { [key: string]: number } = {};
    if (brandRates.length === 1 && brandRates[0].id) {
      revenues[brandRates[0].id] = cardTotal + cashTotal;
      return revenues;
    }
    // If multiple rates, map to first rate or rate matching importKeys
    let mapped = false;
    for (const r of brandRates) {
      if (r.id && (r.importKeys?.includes('카드') || r.importKeys?.includes('Card') || r.name.toLowerCase().includes('card'))) {
        revenues[r.id] = (revenues[r.id] || 0) + cardTotal;
        mapped = true;
      } else if (r.id && (r.importKeys?.includes('현금') || r.importKeys?.includes('Cash') || r.name.toLowerCase().includes('cash'))) {
        revenues[r.id] = (revenues[r.id] || 0) + cashTotal;
        mapped = true;
      }
    }
    if (!mapped && brandRates[0]?.id) {
      revenues[brandRates[0].id] = cardTotal + cashTotal;
    }
    return revenues;
  }
}

export interface GenerationSnapshot {
  totalRevenue: number;
  totalFee: number;
  amountPaid: number;
  remainingBalance: number;
  status: string;
}

export interface GenerationMissingDevice {
  deviceId: string;
  deviceName: string;
  storeId: string;
  storeName: string;
}

export interface GenerationCompanyPlan {
  companyId: string;
  companyName: string;
  classification: GenerationClass;
  keptReason?: string;
  existingInvoiceId: string | null;
  emailed: boolean;
  missingDevices: GenerationMissingDevice[];
  storeItems: { storeId: string, storeName: string, revenues: { [key: string]: number } }[];
  deleteAggIds: string[];
  before: GenerationSnapshot | null;
  after: GenerationSnapshot | null;
  beginningBalance: number;
  totals: GeneratedTotals | null;
  totalDue: number;
  unchanged: boolean;
  forward: { affectedPeriods: string[], latestRemainingChange: number };
}

export interface GenerationPlan {
  period: string;
  brandName: string;
  brandId?: string;
  companies: GenerationCompanyPlan[];
  fingerprint: string;
}

export interface GenerationOutcome {
  companyId: string;
  companyName: string;
  outcome: 'created' | 'refreshed' | 'unchanged' | 'kept-manual' | 'kept-canceled' | 'failed';
  reason: string;
  partial: boolean;
  missingDevices: GenerationMissingDevice[];
}

export interface GenerationResult {
  generationRunId: string;
  outcomes: GenerationOutcome[];
  counts: { created: number, refreshed: number, unchanged: number, kept: number, partial: number, failed: number };
}

/** Compares a preview with its re-plan; covers every value the run would write. */
function planFingerprint(companies: GenerationCompanyPlan[]): string {
  return JSON.stringify(companies.map(c => [
    c.companyId, c.classification, c.existingInvoiceId, c.deleteAggIds, c.storeItems,
    c.missingDevices.map(m => m.deviceId), c.before, c.after, c.beginningBalance
  ]));
}

/** Source reports or invoice state changed between the Generate preview and its confirmation. */
export class StaleGenerationPlanError extends Error {
  constructor() {
    super('Source data changed since the preview. Refresh the preview before applying.');
    this.name = 'StaleGenerationPlanError';
  }
}
